import React from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import { useGetReportObjectPreview } from '../../InfographicBlocksDetailsView/hooks/useGetReportObjectPreview'
import { useAppContext } from '../../../../../../redux/slices/appContext'
import Skeleton from '../../../../../atoms/Skeleton'
import { useGetReportObjectValues } from '../../hooks/useGetReportObjectValues'
import { HiddenInHtmlLabel } from './HiddenInHtmlLabel'

const useStyles = makeStyles(() => ({
  blockContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '0 1rem',
    position: 'relative'
  },
  blockContentInner: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: '.25rem',
    lineHeight: '1em'
  },
  blockContentName: {
    width: '100%',
    lineHeight: '1.4em',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  reportObjectPreview: {
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    position: 'absolute',
    backgroundSize: 'cover',
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'var(--color-surface-400)',
    zIndex: 1
  },
  previewPlaceholder: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& .MuiSkeleton-root': {
      position: 'absolute',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    }
  }
}))
export const ReportObjectBlock = ({ infographicBlockId, runtimeConfigValues, reportObject, containerSize, isEditable }) => {
  const classes = useStyles()
  const { clientId, availableDates } = useAppContext()

  const reportObjectConfigs = useGetReportObjectValues(reportObject?.component, runtimeConfigValues)

  const { data: preview, isFetching: isLoadingPreview } = useGetReportObjectPreview({
    reportObjectName: reportObject.component,
    infographicBlockId: infographicBlockId,
    inputParams: {
      clientId,
      startDate: availableDates.mainDate,
      endDate: availableDates.mainDate,
      size: {
        cols: reportObject?.size?.cols,
        rows: reportObject?.size?.rows,
        ...(containerSize && {})
      },
      props: reportObject?.props ?? {},
      ...(reportObjectConfigs ?? {})
    }
  }, {
    enabled: !!reportObject?.component?.length
  })

  if (reportObject.isEmptyBlock && isEditable) {
    return (
      <div className={classes.blockContent}>
        <div className={classes.blockContentInner}>
          <span
            className={classes.blockContentName}
            style={{ fontSize: Math.min(Math.max(12, (reportObject?.size?.cols ?? 0) * 4), 36) + 'px' }}
          >
            Click here to add a<br />new report object
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.blockContent}>
      <div className={classes.blockContentInner}>
        <span
          className={classes.blockContentName}
          style={{ fontSize: Math.min(Math.max(12, (reportObject?.size?.cols ?? 0) * 4), 36) + 'px' }}
        >
          {!reportObject.component?.length && isEditable && (
            <div>
              Select a component<br />
              for this object
            </div>
          )}
          {isLoadingPreview && (
            <div className={classes.previewPlaceholder}>
              <Skeleton height='100%' />
              <CircularProgress />
            </div>
          )}
          {preview?.previewImageUrl && (
            <div
              style={{ backgroundImage: `url('${preview.previewImageUrl}')` }}
              title={reportObject.component}
              className={classes.reportObjectPreview}
            />
          )}
          {!preview?.previewImageUrl && !isLoadingPreview && reportObject?.component}
        </span>
        {reportObject.hideInHtml && (
          <HiddenInHtmlLabel />
        )}
      </div>
    </div>
  )
}

ReportObjectBlock.propTypes = {
  infographicBlockId: PropTypes.number,
  reportObject: PropTypes.shape({
    component: PropTypes.string,
    size: PropTypes.shape({
      cols: PropTypes.number,
      rows: PropTypes.number
    }),
    isEmptyBlock: PropTypes.bool,
    props: PropTypes.object,
    hideInHtml: PropTypes.bool
  }),
  containerSize: {
    cols: PropTypes.number,
    rows: PropTypes.number
  },
  runtimeConfigValues: PropTypes.object,
  isEditable: PropTypes.bool
}
